"use client";

import React from "react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { PostsProvider, usePosts } from "@/integrations/wordpress/WordPressPostsProvider";
import { WP_Query } from "@/integrations/wordpress/wp_query";
import { ArrowRight } from "lucide-react";
function JournalPostsConsumer() {
  const {
    posts,
    loading,
    error
  } = usePosts();
  if (loading) {
    return <div data-section-id="225826" className="animate-pulse space-y-12">
        <div className="grid grid-cols-1 gap-8 lg:grid-cols-3">
          <div className="h-[60vh] rounded-none bg-muted lg:col-span-2"></div>
          <div className="h-[60vh] rounded-none bg-muted lg:col-span-1 hidden lg:block"></div>
        </div>
        <div className="columns-1 gap-8 md:columns-2 lg:columns-3">
          {[...Array(3)].map((_, i) => <div key={i} className="mb-8 h-64 rounded-none bg-muted break-inside-avoid"></div>)}
        </div>
      </div>;
  }
  if (error) {
    return <div className="flex flex-col items-center justify-center py-20 text-center">
        <p className="text-muted-foreground mb-4">Impossibile caricare le cronache di vetta in questo momento.</p>
      </div>;
  }
  if (!posts || posts.length === 0) {
    return <div className="flex flex-col items-center justify-center py-20 text-center">
        <p className="text-muted-foreground mb-4">Nessun diario di cammino ancora pubblicato.</p>
      </div>;
  }
  const featuredPost = posts[0];
  const secondaryPost = posts.length > 1 ? posts[1] : null;
  const gridPosts = posts.slice(2);
  const formatDate = (dateString?: string) => {
    if (!dateString) return "";
    try {
      return new Date(dateString).toLocaleDateString("it-IT", {
        year: "numeric",
        month: "long",
        day: "numeric"
      });
    } catch (e) {
      return dateString;
    }
  };
  const getCategory = (post: any) => {
    return post.categories && post.categories.length > 0 ? post.categories[0].name.toUpperCase() : "CRONACA DI VETTA";
  };
  return <div className="space-y-16">
      {}
      <div className="grid grid-cols-1 gap-8 lg:grid-cols-3">
        {}
        {featuredPost && <article className="group lg:col-span-2 flex flex-col h-full">
            <Link to={featuredPost.link || "#"} className="block overflow-hidden relative aspect-[16/9] lg:aspect-auto lg:h-[600px] mb-6 rounded-lg shadow-lg">
              <img src={featuredPost.featuredImage || "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1648136/img/handpan_summit_performance.png"} alt={featuredPost.title} className="w-full h-full object-cover transition-transform duration-1000 ease-out group-hover:scale-105" />
              <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent opacity-60" />
            </Link>
            <div className="transition-transform duration-500 ease-out group-hover:-translate-y-[5px] flex flex-col flex-grow">
              <div className="flex items-center gap-4 mb-3">
                <span className="text-xs font-sans font-semibold tracking-widest text-primary">
                  {getCategory(featuredPost)}
                </span>
                <span className="text-xs font-sans text-muted-foreground">
                  {formatDate(featuredPost.date)}
                </span>
              </div>
              <Link to={featuredPost.link || "#"} className="block">
                <h3 className="font-default text-3xl md:text-5xl font-medium leading-tight mb-4 text-foreground">
                  {featuredPost.title}
                </h3>
              </Link>
              {featuredPost.excerpt && <p className="text-muted-foreground font-serif line-clamp-3 max-w-2xl text-lg italic">
                  {featuredPost.excerpt.replace(/(<([^>]+)>)/gi, "")}
                </p>}
            </div>
          </article>}

        {}
        {secondaryPost && <article className="group lg:col-span-1 flex flex-col h-full">
            <Link to={secondaryPost.link || "#"} className="block overflow-hidden relative aspect-[4/3] lg:aspect-auto lg:h-[400px] mb-6 rounded-lg shadow-md">
              <img src={secondaryPost.featuredImage || "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1648136/img/alpini_historical_archive.png"} alt={secondaryPost.title} className="w-full h-full object-cover transition-transform duration-1000 ease-out group-hover:scale-105" />
              <div className="absolute inset-0 bg-gradient-to-t from-black/40 to-transparent opacity-40" />
            </Link>
            <div className="transition-transform duration-500 ease-out group-hover:-translate-y-[5px] flex flex-col flex-grow">
              <div className="flex items-center gap-4 mb-3">
                <span className="text-xs font-sans font-semibold tracking-widest text-primary">
                  {getCategory(secondaryPost)}
                </span>
                <span className="text-xs font-sans text-muted-foreground">
                  {formatDate(secondaryPost.date)}
                </span>
              </div>
              <Link to={secondaryPost.link || "#"} className="block">
                <h3 className="font-default text-2xl md:text-3xl font-medium leading-tight mb-3 text-foreground">
                  {secondaryPost.title}
                </h3>
              </Link>
            </div>
          </article>}
      </div>

      {}
      {gridPosts.length > 0 && <div className="columns-1 gap-8 md:columns-2 lg:columns-3 pt-8 border-t border-border/30">
          {gridPosts.map((post, i) => <article key={post.id} data-index={i} className="group mb-12 break-inside-avoid flex flex-col">
              <Link to={post.link || "#"} className="block overflow-hidden relative mb-5 rounded-md shadow-sm">
                <img src={post.featuredImage || `/img/mountain_trail_memory_${i % 3 + 1}.png`} alt={post.title} className="w-full h-auto object-cover transition-transform duration-1000 ease-out group-hover:scale-105" />
              </Link>
              <div className="transition-transform duration-500 ease-out group-hover:-translate-y-[5px]">
                <div className="flex items-center gap-3 mb-2">
                  <span className="text-[10px] font-sans font-semibold tracking-widest text-primary">
                    {getCategory(post)}
                  </span>
                  <span className="text-[10px] font-sans text-muted-foreground">
                    {formatDate(post.date)}
                  </span>
                </div>
                <Link to={post.link || "#"} className="block">
                  <h4 className="font-default text-xl md:text-2xl font-medium leading-tight text-foreground">
                    {post.title}
                  </h4>
                </Link>
              </div>
            </article>)}
        </div>}

      <div className="flex justify-center pt-8">
        <Button variant="outline" size="lg" className="rounded-md border-primary/30 hover:border-primary hover:bg-accent/10 font-sans uppercase tracking-widest text-xs px-8 transition-all duration-300" asChild>
          <Link to="#">
            Esplora l'Archivio <ArrowRight className="ml-2 h-4 w-4" />
          </Link>
        </Button>
      </div>
    </div>;
}
export default function TheJournal() {
  const wp_query_params = {
    post_type: "post",
    per_page: 8
  };
  const wp_query = new WP_Query(wp_query_params);
  return <section id="diario-di-vetta" className="bg-background text-foreground py-20 lg:py-32">
      <div className="container mx-auto px-4 md:px-6 max-w-7xl">
        <div className="mb-16 md:mb-24 flex flex-col md:flex-row md:items-end justify-between gap-6">
          <div className="max-w-2xl">
            <h2 className="font-default text-4xl md:text-6xl font-medium text-foreground mb-4">
              Diario di Vetta
            </h2>
            <p className="text-muted-foreground font-serif text-lg italic">
              Cronache di ascese, risonanze metalliche tra le rocce e memorie silenziose dei caduti sui monti del vicentino.
            </p>
          </div>
        </div>

        <PostsProvider wp_query={{
        post_type: "post",
        per_page: 8
      }}>
          <JournalPostsConsumer />
        </PostsProvider>
      </div>
    </section>;
}